import { createAction, handleActions } from '~shared/utils/reduxActions';
import ActionTypes from '~shared/constants/actionTypes';
import {
  _createChatSession,
  _createChatMessage,
  _retryMessage,
  _getChatSessionMessages,
  _savePartialMessage,
  _completeChatMessage,
  _getChatSessions,
  _setChatPinned,
  _reorderPinnedChats,
  _submitMessageFeedback,
  _chatHistoryContents,
} from '~shared/httpservice/apis';
import {
  classifyStreamError,
  STREAM_ERROR_BUFFER_SIZE,
} from './streamErrorClassifier';
import Toast from '~shared/toast';

// ─────────────────────────────────────────────────────────────────────────────
// Initial state
// ─────────────────────────────────────────────────────────────────────────────
const initialState = {
  // ── Legacy chat state (unchanged) ────────────────────────────────────────
  chatResponse: [],
  loading: false,
  personality: '',
  conversation_id: null,
  tab_id: null,
  search_query: '',
  new_chat: true,
  country: '',
  region: '',
  topic: '',
  previous_text: '',
  paginate_count: 0,
  generate_more: false,
  cancel_api: false,
  permission: false,
  uploadedDocs: [],
  docSubmitLoading: false,
  docUrl: '',
  uploadDocLoading: false,

  // ── Unified streaming chat state (Task 7) ────────────────────────────────
  session_id: null,           // UUID of current ChatSession (null = no active session)
  current_message_id: null,   // UUID of ChatMessage currently being streamed
  is_streaming: false,        // true while a fetch stream is open
  // true from a clean end of stream until the completion save lands, so the next
  // question isn't sent before this answer's references reach Django (the next
  // turn's reference numbering continues from them).
  is_saving_completion: false,
  is_session_loading: false,  // true while loading a past chat from history

  // ── Sidebar chat list (v1/chat/sessions/, a page at a time) ─────────────
  sidebar_pinned: [],             // pinned sessions, top pin first (all of them)
  sidebar_chats: [],              // unpinned streaming sessions, newest activity first
  sidebar_next_cursor: null,      // pass to the next page request; null = all loaded
  sidebar_has_older_chats: false, // user has legacy chats → show "Older chats"
  sidebar_loading: false,         // first load, nothing to show yet
  sidebar_loading_more: false,    // "Load more" in flight
  // stream_abort is intentionally NOT in Redux state — AbortController can't be
  // serialized by redux-persist. It is stored as a module-level ref instead.
};

// Module-level AbortController ref (not in Redux — not serializable)
let _abortController = null;
// Latches when Stop is clicked before the fetch begins, so _executeStream
// can abort itself as soon as the controller exists.
let _stopRequested = false;
// Why the current stream is being aborted. 'user_stop' = the user deliberately
// pressed Stop (the backend will HONOUR it and keep the partial). Any other
// value is an involuntary interruption (session switch / route-away) where the
// AI callback remains the source of truth and overrides the partial. Default is
// the safe involuntary value — only the Stop button sets 'user_stop'.
let _abortReason = 'navigation';
// When a session switch aborts an in-flight stream, the abort handler in
// _executeStream cannot read the partial from chatResponse (the switch clears
// it). Capture the partial here BEFORE clearing so the abort path can save it.
let _capturedPartialOnAbort = null;

// RAF token-batching — accumulate per-frame instead of dispatching per token.
let _tokenBuffer    = '';
let _thinkingBuffer = '';
let _rafId          = null;
let _rafDispatch    = null; // set to dispatchIfActive for the active stream

// ─────────────────────────────────────────────────────────────────────────────
// _abortStreamForSessionSwitch — abort the in-flight stream synchronously and
// capture the partial answer so _executeStream's AbortError handler can save
// it to the backend before the caller clears chatResponse.
//
// Also synchronously dispatches streamStopped so is_streaming flips to false
// while session_id still matches the abandoned stream — otherwise startNewChat
// (which nulls session_id) would leave the new chat showing a stuck spinner.
// ─────────────────────────────────────────────────────────────────────────────
function _abortStreamForSessionSwitch(dispatch, getState) {
  _stopRequested = true;
  _abortReason   = 'navigation';  // involuntary — the AI callback stays source of truth
  if (!_abortController) {
    // _executeStream hasn't started yet — _stopRequested will cause it to abort
    // immediately when it does. Clear the spinner now so the UI doesn't hang.
    const state = getState()?.chat;
    if (state?.is_streaming) {
      dispatch(streamStopped({ partial_answer: '', reason: 'navigation' }));
    }
    return;
  }
  const state = getState()?.chat;
  if (state?.is_streaming) {
    const lastMsg = state.chatResponse?.[state.chatResponse.length - 1];
    const partial = lastMsg?.answer || '';
    _capturedPartialOnAbort = {
      answer: partial,
      references: lastMsg?.references || [],
    };
    dispatch(streamStopped({ partial_answer: partial, reason: 'navigation' }));
  }
  _abortController.abort();
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy action creators (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const setChatResponse = createAction(ActionTypes.SET_CHAT_RESPONSE);
export const addChatResponse = (data) => (dispatch) => {
  dispatch(setChatResponse(data));
};

const setClearChat = createAction(ActionTypes.SET_CLEAR_CHAT);
export const clearChat = () => (dispatch) => {
  dispatch(setClearChat());
};

const setLoadingAction = createAction(ActionTypes.SET_LOADING);
export const setLoading = (data) => (dispatch) => {
  dispatch(setLoadingAction(data));
};

const setPersonalityAction = createAction(ActionTypes.SET_PERSONALITY);
export const setPersonality = (data) => (dispatch) => {
  dispatch(setPersonalityAction(data));
};

const setConversationIdAction = createAction(ActionTypes.SET_CONVERSATION_ID);
export const setConversationId = (data) => (dispatch) => {
  dispatch(setConversationIdAction(data));
};

const setTabIdAction = createAction(ActionTypes.SET_TAB_ID);
export const setTabId = (data) => (dispatch) => {
  dispatch(setTabIdAction(data));
};

const setSearchQueryAction = createAction(ActionTypes.SET_SEARCH_QUERY);
export const setSearchQuery = (data) => (dispatch) => {
  dispatch(setSearchQueryAction(data));
};

const newChatAction = createAction(ActionTypes.SET_NEW_CHAT);
export const setNewChat = (data) => (dispatch) => {
  dispatch(newChatAction(data));
};

const chatCountry = createAction(ActionTypes.SET_CHAT_COUNTRY);
export const setChatCountry = (data) => (dispatch) => {
  dispatch(chatCountry(data));
};

const chatRegion = createAction(ActionTypes.SET_CHAT_REGION);
export const setChatRegion = (data) => (dispatch) => {
  dispatch(chatRegion(data));
};

const chatTopic = createAction(ActionTypes.SET_CHAT_TOPIC);
export const setChatTopic = (data) => (dispatch) => {
  dispatch(chatTopic(data));
};

const previousText = createAction(ActionTypes.SET_PREVIOUS_TEXT);
export const setPreviousText = (data) => (dispatch) => {
  dispatch(previousText(data));
};

const paginateCount = createAction(ActionTypes.SET_PAGINATE_COUNT);
export const setPaginateCount = (data) => (dispatch) => {
  dispatch(paginateCount(data));
};

const generateMore = createAction(ActionTypes.SET_GENERATE_MORE);
export const setGenerateMore = (data) => (dispatch) => {
  dispatch(generateMore(data));
};

const cancelApi = createAction(ActionTypes.SET_CANCEL_API);
export const setCancelApi = (data) => (dispatch) => {
  dispatch(cancelApi(data));
};

const chatPermission = createAction(ActionTypes.SET_CHAT_PERMISSION);
export const setChatPermission = (data) => (dispatch) => {
  dispatch(chatPermission(data));
};

const docSubmitLoading = createAction(ActionTypes.SET_DOCSUBMIT_LOADING);
export const setDocSubmitLoading = (data) => (dispatch) => {
  dispatch(docSubmitLoading(data));
};

const uploadDocLoading = createAction(ActionTypes.SET_UPLOADDOC_LOADING);
export const setUploadDocLoading = (data) => (dispatch) => {
  dispatch(uploadDocLoading(data));
};

const uploadedDocs = createAction(ActionTypes.SET_UPLOADEDDOC);
export const setUploadedDocs = (data) => (dispatch) => {
  dispatch(uploadedDocs(data));
};

const docurl = createAction(ActionTypes.SET_DOC_URL);
export const setDocurl = (data) => (dispatch) => {
  dispatch(docurl(data));
};

// ─────────────────────────────────────────────────────────────────────────────
// Streaming action creators (Task 7)
// ─────────────────────────────────────────────────────────────────────────────
const streamSessionCreated = createAction(ActionTypes.STREAM_SESSION_CREATED);
const streamInit           = createAction(ActionTypes.STREAM_INIT);
const streamRetryInit      = createAction(ActionTypes.STREAM_RETRY_INIT);
const streamToken          = createAction(ActionTypes.STREAM_TOKEN);
const streamThinking       = createAction(ActionTypes.STREAM_THINKING);
const streamReferences     = createAction(ActionTypes.STREAM_REFERENCES);
const streamFileDownloads  = createAction(ActionTypes.STREAM_FILE_DOWNLOADS);
const streamDone           = createAction(ActionTypes.STREAM_DONE);
const streamError          = createAction(ActionTypes.STREAM_ERROR);
const streamInlineError    = createAction(ActionTypes.STREAM_INLINE_ERROR);
const streamStopped        = createAction(ActionTypes.STREAM_STOPPED);
const streamMessageIdSet   = createAction(ActionTypes.STREAM_MESSAGE_ID_SET);
const loadSessionMessages  = createAction(ActionTypes.LOAD_SESSION_MESSAGES);
const sessionLoadStart     = createAction(ActionTypes.SESSION_LOAD_START);
const sessionLoadEnd       = createAction(ActionTypes.SESSION_LOAD_END);
const completionSaveStart  = createAction(ActionTypes.COMPLETION_SAVE_START);
const completionSaveEnd    = createAction(ActionTypes.COMPLETION_SAVE_END);

// Upper bound on how long the input waits for the completion save. Django
// itself holds the next turn until an answer is saved (409 + retry, below), so
// this wait only spares that round trip; a slow save must never lock the input.
const COMPLETION_SAVE_WAIT_MS = 5000;
const _withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(resolve, ms)),
]);
const messageRatingSet     = createAction(ActionTypes.MESSAGE_RATING_SET);

// Django answers 409 while an earlier answer in the session is still being
// saved, because this turn's reference numbering must include its references
// (the AI callback can arrive late). Each request already waits ~5 s on the
// server, so a handful of retries covers Django's 30 s stale cutoff.
const PREVIOUS_ANSWER_RETRY_LIMIT_MS = 40000;
const PREVIOUS_ANSWER_RETRY_DELAY_MS = 1000;

// Runs `request`, retrying while Django returns 409. Calls `onWait` once on the
// first 409 and resolves null if `shouldStop()` turns true between attempts.
export const requestWhilePreviousAnswerSaves = async (request, {
  onWait,
  shouldStop,
  limitMs = PREVIOUS_ANSWER_RETRY_LIMIT_MS,
  delayMs = PREVIOUS_ANSWER_RETRY_DELAY_MS,
} = {}) => {
  const startedAt = Date.now();
  let notified = false;
  for (;;) {
    try {
      return await request();
    } catch (err) {
      if (err?.response?.status !== 409) throw err;
      if (Date.now() - startedAt >= limitMs) {
        throw new Error(err.response.data?.message || 'The previous answer is still being saved.');
      }
      if (!notified) {
        notified = true;
        if (onWait) onWait();
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      if (shouldStop && shouldStop()) return null;
    }
  }
};
const _previousAnswerWait = {
  onWait: () => Toast.info('Finishing the previous answer…'),
  shouldStop: () => _stopRequested,
};

// ─────────────────────────────────────────────────────────────────────────────
// resetStaleStreamingState — clear is_streaming / is_session_loading flags
// that may have survived from a previous tab via redux-persist. The module
// state (AbortController, timers) is fresh after every page load, so any
// persisted "in-flight" flag is by definition lying.
// ─────────────────────────────────────────────────────────────────────────────
export const resetStaleStreamingState = () => (dispatch, getState) => {
  const chat = getState()?.chat;
  if (!chat) return;
  if (chat.is_saving_completion) dispatch(completionSaveEnd());
  if (chat.is_streaming || chat.is_session_loading) {
    dispatch(sessionLoadEnd());
    dispatch(streamStopped({
      partial_answer: chat.chatResponse?.[chat.chatResponse.length - 1]?.answer || '',
      reason: 'navigation',
    }));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper — resolve auth token from either SSO or regular signin
// ─────────────────────────────────────────────────────────────────────────────
function _getToken(getState) {
  const auth = getState()?.auth;
  return (
    auth?.sso_login_callback?.response?.data?.data?.token ||
    auth?.signin?.response?.data?.data?.token ||
    ''
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper — build messages array for Kush AI in Vercel AI SDK format
//
// Upstream is stateful and looks up prior turns by conversation_id, so we only
// send the new user question. If a follow-up question loses context, revert
// this to replay the full transcript from `existingMessages`.
// ─────────────────────────────────────────────────────────────────────────────
function _buildKushMessages(existingMessages, newQuestion) {
  return [{ role: 'user', content: newQuestion }];
}

// ─────────────────────────────────────────────────────────────────────────────
// _flushTokenBuffers — drain accumulated token/thinking buffers.
// Called by requestAnimationFrame (~16ms) and synchronously before references
// or done are dispatched to preserve Redux ordering.
// ─────────────────────────────────────────────────────────────────────────────
function _flushTokenBuffers() {
  _rafId = null;
  if (_tokenBuffer && _rafDispatch) {
    _rafDispatch(streamToken(_tokenBuffer));
    _tokenBuffer = '';
  }
  if (_thinkingBuffer && _rafDispatch) {
    _rafDispatch(streamThinking(_thinkingBuffer));
    _thinkingBuffer = '';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Vercel AI SDK Data Stream Protocol parser
//
// Each line is one of:
//   0:"<text chunk>"   → answer / thinking text
//   2:[<json>]         → references array
//   3:""               → done signal
//
// The upstream service has no dedicated error channel — when generation fails
// it emits the error as a channel-0 text chunk (e.g. "**[System Error]** ..."),
// which would otherwise be rendered as part of the assistant message. We
// maintain a small rolling buffer of channel-0 text and run it through
// classifyStreamError on every chunk so the sentinel can be detected even when
// it spans two chunks.
// ─────────────────────────────────────────────────────────────────────────────
function _parseLine(line, dispatch, parserState, onInlineErrorDetected) {
  if (!line) return parserState;
  if (parserState.inlineErrorFired) return parserState;

  if (line.startsWith('0:')) {
    // Extract the JSON-string value after the channel prefix
    try {
      const raw = JSON.parse(line.slice(2));  // e.g. "chunk text"

      // Maintain a rolling buffer to detect error sentinels that span chunks.
      const newBuffer = (parserState.channel0Buffer + raw).slice(-STREAM_ERROR_BUFFER_SIZE);
      const detection = classifyStreamError(newBuffer);
      if (detection) {
        // Flush buffered tokens first (as the 2:/3: paths do) so the answer holds every earlier chunk.
        // This chunk is never appended, so only roll back the part of the error text from earlier chunks.
        if (_rafId) { cancelAnimationFrame(_rafId); _flushTokenBuffers(); }
        const rollbackChars = Math.max(0, newBuffer.length - raw.length - detection.matchStart);
        dispatch(streamInlineError({
          code: detection.code,
          friendlyMessage: detection.friendlyMessage,
          technicalDetails: detection.technicalDetails,
          rollbackChars,
        }));
        if (onInlineErrorDetected) onInlineErrorDetected();
        return { ...parserState, channel0Buffer: newBuffer, inlineErrorFired: true };
      }

      // Track whether we are inside a <thinking>…</thinking> block. A chunk can hold text on
      // both sides of a tag, so route each piece by the tags before it.
      let inThinking = parserState.inThinking;
      let routed = false;
      raw.split(/(<\/?thinking>)/).forEach((part) => {
        if (part === '<thinking>') {
          inThinking = true;
        } else if (part === '</thinking>') {
          inThinking = false;
        } else if (part) {
          if (inThinking) {
            _thinkingBuffer += part;
          } else {
            _tokenBuffer += part;
          }
          routed = true;
        }
      });
      if (routed && !_rafId) _rafId = requestAnimationFrame(_flushTokenBuffers);
      return { ...parserState, channel0Buffer: newBuffer, inThinking };
    } catch (_) {
      return parserState;
    }
  }

  if (line.startsWith('2:')) {
    try {
      const parsed = JSON.parse(line.slice(2));
      // Upstream wraps payloads in an array-of-one object:
      //   [{ references: [...], file_downloads: [...] }]
      // - references: source citations
      // - file_downloads: CSV / PPTX / DOCX artifacts to render as chips
      // Charts/images are NOT in this payload — they render from the inline
      // ![](s3://...) markdown in the answer text instead.
      // The "charts" key is the pre-rename name; kept as a fallback for any
      // in-flight progressive frames during the transition.
      const refs = Array.isArray(parsed) && parsed[0]?.references
        ? parsed[0].references
        : Array.isArray(parsed) ? parsed : [];
      if (_rafId) { cancelAnimationFrame(_rafId); _flushTokenBuffers(); }
      dispatch(streamReferences(refs));

      const fdArr =
        (Array.isArray(parsed) && Array.isArray(parsed[0]?.file_downloads) && parsed[0].file_downloads)
        || (Array.isArray(parsed) && Array.isArray(parsed[0]?.charts) && parsed[0].charts)
        || [];
      const fds = fdArr
        .filter((c) => c && c.type === 'file_download' && c.url)
        .map((c) => ({ url: c.url, filename: c.filename, label: c.label }));
      if (fds.length) dispatch(streamFileDownloads(fds));
    } catch (_) { /* ignore malformed */ }
    return parserState;
  }

  if (line.startsWith('3:')) {
    if (_rafId) { cancelAnimationFrame(_rafId); _flushTokenBuffers(); }
    dispatch(streamDone());
    return parserState;
  }

  return parserState;
}

// ─────────────────────────────────────────────────────────────────────────────
// _executeStream — handles the fetch + ReadableStream read loop.
// Caller is responsible for obtaining stream credentials and dispatching the
// init action (streamInit or streamRetryInit) before calling this.
// ─────────────────────────────────────────────────────────────────────────────
async function _executeStream(dispatch, getState, { streamUrl, streamToken, body, sessionId, messageId }) {
  const token = _getToken(getState);

  _abortController = new AbortController();
  if (_stopRequested) {
    _abortController.abort();
  }

  // Hoisted so the catch block can distinguish an upstream-error abort from a
  // user-Stop abort (both surface as AbortError).
  let inlineErrorAborted = false;

  // Drop any stream-side dispatch if the user has navigated to a different
  // session (or no session) while this fetch is still running. Without this
  // guard, tokens / terminal actions bleed into whatever message currently
  // sits at chatResponse[last], corrupting the other session's transcript.
  const dispatchIfActive = (action) => {
    if (getState()?.chat?.session_id === sessionId) {
      return dispatch(action);
    }
    return undefined;
  };

  // Wire RAF batching to this stream's session-guarded dispatcher.
  _rafDispatch    = dispatchIfActive;
  _tokenBuffer    = '';
  _thinkingBuffer = '';

  // Helper to read the partial answer for save-on-abort. Prefers a snapshot
  // captured by _abortStreamForSessionSwitch (taken before the feed was
  // cleared); otherwise reads from current state.
  const readPartialForSave = () => {
    if (_capturedPartialOnAbort) {
      const captured = _capturedPartialOnAbort;
      _capturedPartialOnAbort = null;
      return { partialAnswer: captured.answer, refs: captured.references };
    }
    const state = getState()?.chat;
    const lastMsg = state?.chatResponse?.[state.chatResponse.length - 1];
    return {
      partialAnswer: lastMsg?.answer || '',
      refs: lastMsg?.references || [],
    };
  };

  try {
    const fetchRes = await fetch(streamUrl, {
      method: 'POST',
      headers: {
        'Authorization': streamToken,
        'Content-Type': 'application/json',
      },
      body,
      signal: _abortController.signal,
    });

    if (!fetchRes.ok) {
      dispatchIfActive(streamError(`Stream request failed: ${fetchRes.status}`));
      return;
    }

    // Read and parse the ReadableStream line by line
    const reader = fetchRes.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let parserState = {
      inThinking: false,
      channel0Buffer: '',
      inlineErrorFired: false,
    };
    const onInlineErrorDetected = () => {
      inlineErrorAborted = true;
      if (_abortController) _abortController.abort();
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep last incomplete line in buffer

      for (const line of lines) {
        parserState = _parseLine(line.trim(), dispatchIfActive, parserState, onInlineErrorDetected);
      }
    }

    // Flush any remaining buffer content
    if (buffer.trim()) {
      parserState = _parseLine(buffer.trim(), dispatchIfActive, parserState, onInlineErrorDetected);
    }

    // The upstream usually ends the stream right after its error chunk, so the abort
    // may never surface as an AbortError from read(); save the partial either way.
    if (inlineErrorAborted) {
      throw Object.assign(new Error('Inline upstream error'), { name: 'AbortError' });
    }

    // If STREAM_DONE was not emitted by a channel-3 line, emit it now
    if (getState()?.chat?.is_streaming) {
      dispatchIfActive(streamDone());
    }

    // Store message_id for feedback/partial calls
    dispatchIfActive(streamMessageIdSet({ message_id: messageId, session_id: sessionId }));

    // Persist a browser-observed clean EOF immediately. This is best-effort;
    // the authoritative AI callback may still enrich or replace it later.
    if (getState()?.chat?.session_id === sessionId && sessionId && messageId) {
      const messages = getState()?.chat?.chatResponse || [];
      const completedMessage = [...messages].reverse().find(
        (m) => m?.role === 'ai' && m?.message_id === messageId,
      );
      if (completedMessage) {
        // Keep the input disabled until the save lands (capped), so a fast
        // follow-up can't start before these references reach Django.
        dispatch(completionSaveStart());
        try {
          await _withTimeout(
            _completeChatMessage(token, sessionId, messageId, {
              answer: completedMessage.answer || '',
              references: completedMessage.references || [],
              file_downloads: completedMessage.file_downloads || [],
            }),
            COMPLETION_SAVE_WAIT_MS,
          );
        } catch (_) { /* callback remains the fallback */ }
        finally {
          dispatch(completionSaveEnd());
        }
      }
    }

  } catch (err) {
    if (err.name === 'AbortError' && inlineErrorAborted) {
      // Upstream emitted an error mid-stream — STREAM_INLINE_ERROR already
      // dispatched. Persist the partial answer (now with the error blob
      // stripped by the reducer) so a reload preserves what the user saw.
      const { partialAnswer, refs } = readPartialForSave();

      if (sessionId && messageId) {
        try {
          await _savePartialMessage(token, sessionId, messageId, {
            partial_answer: partialAnswer,
            references: refs,
            reason: 'upstream_error',  // involuntary — callback stays source of truth
          });
        } catch (_) { /* best-effort */ }
      }

      dispatchIfActive(streamMessageIdSet({ message_id: messageId, session_id: sessionId }));
    } else if (err.name === 'AbortError') {
      // User clicked Stop — or a session switch aborted us. Save partial.
      // For session-switch aborts, streamStopped has already been dispatched
      // synchronously by _abortStreamForSessionSwitch (so is_streaming flipped
      // to false before session_id changed). Re-dispatching here would either
      // be a no-op or, after a session load, mutate the wrong message — skip
      // it whenever is_streaming is already false.
      const { partialAnswer, refs } = readPartialForSave();

      if (sessionId && messageId) {
        try {
          await _savePartialMessage(token, sessionId, messageId, {
            partial_answer: partialAnswer,
            references: refs,
            // 'user_stop' only when the Stop button was pressed; session-switch
            // aborts default to 'navigation' (involuntary → callback overrides).
            reason: _abortReason,
          });
        } catch (_) { /* best-effort */ }
      }

      if (getState()?.chat?.is_streaming) {
        dispatchIfActive(streamStopped({
          partial_answer: partialAnswer,
          reason: _abortReason,
        }));
      }
    } else {
      // Network drop / fetch failure — save what we have so a reload preserves
      // the partial answer (the row would otherwise sit at status='pending'
      // until the 10-min Celery sweep marks it failed).
      const { partialAnswer, refs } = readPartialForSave();
      if (sessionId && messageId && partialAnswer) {
        try {
          await _savePartialMessage(token, sessionId, messageId, {
            partial_answer: partialAnswer,
            references: refs,
            reason: 'network_error',  // involuntary — callback stays source of truth
          });
        } catch (_) { /* best-effort */ }
      }
      dispatchIfActive(streamStopped({
        partial_answer: partialAnswer,
        reason: 'network_error',
      }));
      if (getState()?.chat?.session_id === sessionId) {
        _startResumePoll(dispatch, getState, sessionId);
      }
    }
  } finally {
    if (_rafId) { cancelAnimationFrame(_rafId); _flushTokenBuffers(); }
    _tokenBuffer    = '';
    _thinkingBuffer = '';
    _rafDispatch    = null;
    _abortController = null;
    _stopRequested = false;
    _abortReason = 'navigation';  // reset to safe involuntary default for the next stream
    _capturedPartialOnAbort = null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// sendStreamMessage — main streaming thunk
// ─────────────────────────────────────────────────────────────────────────────
export const sendStreamMessage = (question, selectedSources = [], options = {}) =>
  async (dispatch, getState) => {
    const token = _getToken(getState);
    if (!token) return;

    const {
      is_deep_research = false,
      is_generate_slides = false,
      is_generate_document = false,
      uploaded_s3_keys = [],
      attachments_metadata = [],
      attachments_preview = [],   // optimistic-render-only (blob URLs etc.)
    } = options;

    _stopRequested = false;

    // 1. Optimistically add user message + empty AI placeholder to the feed.
    // `attachments_preview` is the input-bar tile shape (with blob URL) so
    // the user message renders the file tiles immediately.
    dispatch(streamInit({ question, attachments: attachments_preview }));

    try {
      // 2. Create session if we don't have one yet
      let sessionId = getState()?.chat?.session_id;
      if (!sessionId) {
        const sessionRes = await _createChatSession(token);
        sessionId = sessionRes?.data?.data?.session_id;
        dispatch(streamSessionCreated(sessionId));
      }

      // 3. Create message record on Django, get stream credentials
      const msgRes = await requestWhilePreviousAnswerSaves(() => _createChatMessage(token, sessionId, {
        question,
        selected_sources: selectedSources,
        is_deep_research,
        is_generate_slides,
        is_generate_document,
        uploaded_s3_keys,
        attachments_metadata,
      }), _previousAnswerWait);
      if (!msgRes) return; // Stopped while waiting; stopStream already cleared the spinner.

      const { message_id, conversation_id, stream_url, stream_token } =
        msgRes?.data?.data || {};

      if (!stream_url || !stream_token) {
        dispatch(streamError('Failed to obtain stream credentials from server.'));
        return;
      }

      // Stamp the message_id on the placeholder + state.current_message_id
      // now (not after the stream finishes) so the pagehide / route-away
      // handlers can target the right backend row mid-flight.
      dispatch(streamMessageIdSet({ message_id, session_id: sessionId }));

      // 4. Build request body for Kush AI (Vercel AI SDK format)
      const existingMessages = getState()?.chat?.chatResponse || [];
      const kushMessages = _buildKushMessages(
        existingMessages.slice(0, -2), // exclude the two placeholders just added
        question,
      );

      const body = JSON.stringify({
        conversation_id,
        messages: kushMessages,
        settings: {
          is_deep_research,
          is_generate_slides,
          is_generate_document,
          selected_sources: selectedSources,
          uploaded_s3_keys,
          // When Document mode is on, send the attached document(s)
          // metadata directly in the payload so the upstream model can pick
          // the source file without a second lookup.
          ...(is_generate_document ? { document: attachments_metadata } : {}),
        },
      });

      // 5. Stream it
      await _executeStream(dispatch, getState, {
        streamUrl: stream_url,
        streamToken: stream_token,
        body,
        sessionId,
        messageId: message_id,
      });

    } catch (err) {
      dispatch(streamError(err.message || 'Failed to start stream.'));
    }
  };

// ─────────────────────────────────────────────────────────────────────────────
// retryStreamMessage — re-run a message in-place using the SAME message_id.
//
// Design: calls POST /retry/ which resets the existing ChatMessage row (clears
// answer/status) and returns fresh stream credentials — no new DB record is
// ever created, so reloading the session from the backend can never duplicate.
//
// pairIndex: index of the user message in chatResponse; everything from here
// onward is replaced in Redux with fresh streaming placeholders.
// ─────────────────────────────────────────────────────────────────────────────
export const retryStreamMessage = (question, aiMessageId, sessionId, pairIndex, options = {}) =>
  async (dispatch, getState) => {
    const token = _getToken(getState);
    if (!token || !aiMessageId || !sessionId) return;

    // If options.editedQuestion is set, this is an edit-then-retry: the new
    // text replaces the original question on the backend row AND becomes the
    // new prompt sent upstream. If unset, plain retry semantics.
    const editedQuestion = options.editedQuestion || null;
    const finalQuestion  = editedQuestion || question;

    // Guard: don't allow a retry while another stream is already in flight.
    // Acts as both a double-click guard on the same button and protection
    // against retrying message N while message N+1 is mid-stream.
    if (getState()?.chat?.is_streaming) {
      Toast.warning('Wait for the current response to finish.');
      return;
    }

    _stopRequested = false;

    // 1. Reset placeholders in Redux (same position, no duplication).
    // Preserve the original message_id on the placeholder — the backend
    // reuses the same UUID, and keeping it here means the retry buttons
    // remain clickable even if the credentials POST itself fails (without
    // it, message_id stays null forever and both retry paths no-op).
    // Also preserve the original user message's attachments so the tiles
    // don't visually disappear mid-retry (the upstream payload still gets
    // the same uploaded_s3_keys via the retry endpoint's echo).
    const priorAttachments = (
      pairIndex != null
        ? getState()?.chat?.chatResponse?.[pairIndex]?.attachments
        : undefined
    ) || [];
    dispatch(streamRetryInit({
      question: finalQuestion,
      pairIndex,
      messageId: aiMessageId,
      attachments: priorAttachments,
    }));

    try {
      // 2. Tell Django to reset the existing message and get fresh stream credentials.
      // Response is wrapped by the project's custom renderer:
      //   { status, code, data: { message_id, conversation_id, stream_url,
      //     stream_token, token_expires_at,
      //     settings: { is_deep_research, is_ppt, is_doc,
      //                 selected_sources, uploaded_s3_keys } }, message }
      const retryRes = await requestWhilePreviousAnswerSaves(
        () => _retryMessage(token, sessionId, aiMessageId, editedQuestion),
        _previousAnswerWait,
      );
      if (!retryRes) return; // Stopped while waiting.
      const payload = retryRes?.data?.data || {};
      const { conversation_id, stream_url, stream_token, message_id } = payload;
      const originalSettings = payload.settings || {};

      if (!stream_url || !stream_token) {
        dispatch(streamError('Failed to obtain retry stream credentials.'));
        return;
      }

      // 3. Build conversation history up to (not including) the retried pair
      const existingMessages = getState()?.chat?.chatResponse || [];
      const kushMessages = _buildKushMessages(
        existingMessages.slice(0, -2), // exclude the fresh placeholders just added
        finalQuestion,
      );

      // Re-apply the original request settings echoed by the backend so that
      // a Deep Research / Slides / source-restricted retry runs in the same
      // mode as the original — not silently downgraded to plain search.
      const isDoc = Boolean(originalSettings.is_generate_document);
      const body = JSON.stringify({
        conversation_id,
        messages: kushMessages,
        settings: {
          is_deep_research: Boolean(originalSettings.is_deep_research),
          is_generate_slides: Boolean(originalSettings.is_generate_slides),
          is_generate_document: isDoc,
          selected_sources: originalSettings.selected_sources || [],
          uploaded_s3_keys: originalSettings.uploaded_s3_keys || [],
          ...(isDoc ? { document: originalSettings.document || [] } : {}),
        },
      });

      // 4. Stream it
      await _executeStream(dispatch, getState, {
        streamUrl: stream_url,
        streamToken: stream_token,
        body,
        sessionId,
        messageId: message_id,
      });

    } catch (err) {
      dispatch(streamError(err.message || 'Retry stream failed.'));
    }
  };

// ─────────────────────────────────────────────────────────────────────────────
// stopStream — abort the active fetch stream, or latch a stop request if the
// fetch hasn't started yet (session-create / credential-fetch phase).
// ─────────────────────────────────────────────────────────────────────────────
export const stopStream = (reason = 'navigation') => (dispatch, getState) => {
  _stopRequested = true;
  _abortReason   = reason;  // 'user_stop' only from the Stop button; else involuntary
  if (_abortController) {
    _abortController.abort();
    return;
  }
  // No active fetch yet — mark the in-flight message as stopped so the UI
  // reflects the click immediately. _executeStream will bail out when it
  // eventually creates the controller (see the _stopRequested check there).
  if (getState()?.chat?.is_streaming) {
    dispatch(streamStopped({ partial_answer: '', reason }));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// submitMessageRating — tri-state thumbs up/down with optimistic update.
// Click the active rating to clear it; click the opposite to switch.
// Reverts and surfaces a toast if the API call fails.
// ─────────────────────────────────────────────────────────────────────────────
export const submitMessageRating = (msg, clickedRating) =>
  async (dispatch, getState) => {
    const token = _getToken(getState);
    if (!token || !msg?.message_id || !msg?.session_id) return;

    const previous = msg.rating || null;
    const next = previous === clickedRating ? null : clickedRating;
    if (previous === next) return;

    dispatch(messageRatingSet({ message_id: msg.message_id, rating: next }));
    try {
      await _submitMessageFeedback(token, msg.session_id, msg.message_id, { rating: next });
    } catch (_) {
      dispatch(messageRatingSet({ message_id: msg.message_id, rating: previous }));
      Toast.error("Couldn't save your feedback. Please try again.");
    }
  };

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar chat list — streaming sessions, loaded a page at a time
// ─────────────────────────────────────────────────────────────────────────────
const setChatSidebar = createAction(ActionTypes.SET_CHAT_SIDEBAR);

// Older persisted state may have neither list
const _list = (value) => (Array.isArray(value) ? value : []);
const _sidebarChats = (getState) => _list(getState()?.chat?.sidebar_chats);
const _sidebarPinned = (getState) => _list(getState()?.chat?.sidebar_pinned);
const _without = (chats, id) => chats.filter((chat) => String(chat.id) !== String(id));

// Put `chat` among `chats` (newest activity first) where it belongs. If it's
// older than everything loaded and more pages remain, leave it out: it comes
// with a later page.
export function insertByActivity(chats, chat, hasMorePages) {
  const time = Date.parse(chat.modified_at);
  const at = chats.findIndex((other) => Date.parse(other.modified_at) < time);
  if (at === -1) return hasMorePages ? chats : [...chats, chat];
  return [...chats.slice(0, at), chat, ...chats.slice(at)];
}

// Put a fresh first page in front of the pages already loaded with "Load more".
// Chats only move up the list (continuing one bumps it to the top), so the
// loaded tail stays valid below the new first page: drop what that page now
// holds and keep the rest, with the cursor that continues after them.
export function mergeFirstPage(loaded, loadedCursor, page, pageCursor) {
  if (!pageCursor || page.length === 0) return { chats: page, cursor: pageCursor || null };
  const inPage = new Set(page.map((chat) => String(chat.id)));
  const lastTime = Date.parse(page[page.length - 1].modified_at);
  const tail = loaded.filter((chat) => !inPage.has(String(chat.id)) && Date.parse(chat.modified_at) <= lastTime);
  return tail.length ? { chats: [...page, ...tail], cursor: loadedCursor } : { chats: page, cursor: pageCursor };
}

// Load (or refresh) the first page. `keepLoaded` keeps the pages loaded with
// "Load more" — for refreshes while the sidebar is open. On mount it's false,
// so chats deleted or renamed elsewhere don't linger from the persisted store.
export const fetchSidebarChats = ({ keepLoaded = false } = {}) => async (dispatch, getState) => {
  const token = _getToken(getState);
  if (!token) return;
  // The store is persisted: a "Load more" cut off by a reload would stay busy
  if (!keepLoaded) dispatch(setChatSidebar({ sidebar_loading_more: false }));
  // Skeleton rows only when there's nothing to show yet, not on every refresh
  if (_sidebarChats(getState).length === 0) dispatch(setChatSidebar({ sidebar_loading: true }));
  try {
    const res = await _getChatSessions(token);
    const data = res?.data?.data || {};
    const page = Array.isArray(data.sessions) ? data.sessions : [];
    const { chats, cursor } = keepLoaded
      ? mergeFirstPage(_sidebarChats(getState), getState().chat.sidebar_next_cursor, page, data.next_cursor)
      : { chats: page, cursor: data.next_cursor || null };
    dispatch(setChatSidebar({
      sidebar_pinned: _list(data.pinned),
      sidebar_chats: chats,
      sidebar_next_cursor: cursor,
      sidebar_has_older_chats: !!data.has_older_chats,
    }));
  } catch (_) {
    /* keep what's shown; the next refresh tries again */
  } finally {
    dispatch(setChatSidebar({ sidebar_loading: false }));
  }
};

// Append the next page ("Load more").
export const fetchMoreSidebarChats = () => async (dispatch, getState) => {
  const token = _getToken(getState);
  const { sidebar_next_cursor: cursor, sidebar_loading_more: busy } = getState()?.chat || {};
  if (!token || !cursor || busy) return;
  dispatch(setChatSidebar({ sidebar_loading_more: true }));
  try {
    const res = await _getChatSessions(token, cursor);
    const data = res?.data?.data || {};
    const loaded = _sidebarChats(getState);
    const ids = new Set(loaded.map((chat) => String(chat.id)));
    const more = (Array.isArray(data.sessions) ? data.sessions : []).filter((chat) => !ids.has(String(chat.id)));
    dispatch(setChatSidebar({ sidebar_chats: [...loaded, ...more], sidebar_next_cursor: data.next_cursor || null }));
  } catch (_) {
    Toast.error("Couldn't load more chats. Please try again.");
  } finally {
    dispatch(setChatSidebar({ sidebar_loading_more: false }));
  }
};

// Local updates after a rename or delete, so the list doesn't refetch.
export const renameSidebarChat = (id, title) => (dispatch, getState) => {
  const rename = (chat) => (String(chat.id) === String(id) ? { ...chat, title } : chat);
  dispatch(setChatSidebar({
    sidebar_chats: _sidebarChats(getState).map(rename),
    sidebar_pinned: _sidebarPinned(getState).map(rename),
  }));
};

export const removeSidebarChat = (id) => (dispatch, getState) => {
  dispatch(setChatSidebar({
    sidebar_chats: _without(_sidebarChats(getState), id),
    sidebar_pinned: _without(_sidebarPinned(getState), id),
  }));
};

// Pin (to the top of Pinned) or unpin (back among the chats by last
// activity). The chat moves straight away; if saving fails it moves back and
// a toast says so.
export const setChatPinned = (chat, pinned) => async (dispatch, getState) => {
  const token = _getToken(getState);
  if (!token) return;
  const before = { sidebar_chats: _sidebarChats(getState), sidebar_pinned: _sidebarPinned(getState) };
  const others = { chats: _without(before.sidebar_chats, chat.id), pinned: _without(before.sidebar_pinned, chat.id) };
  if (pinned) {
    dispatch(setChatSidebar({ sidebar_pinned: [{ ...chat, pinned: true }, ...others.pinned], sidebar_chats: others.chats }));
  } else {
    const hasMorePages = !!getState().chat.sidebar_next_cursor;
    dispatch(setChatSidebar({
      sidebar_pinned: others.pinned,
      sidebar_chats: insertByActivity(others.chats, { ...chat, pinned: false }, hasMorePages),
    }));
  }
  try {
    await _setChatPinned(token, chat.id, pinned);
  } catch (_) {
    dispatch(setChatSidebar(before));
    Toast.error(pinned ? "Couldn't pin the chat. Please try again." : "Couldn't unpin the chat. Please try again.");
  }
};

// Move a pinned chat from one place in Pinned to another (drag and drop, or
// Move up / Move down). It moves straight away; if saving fails it moves back
// and a toast says so. A 409 means the pins changed elsewhere (another tab),
// so the lists reload too.
export const reorderPinnedChats = (from, to) => async (dispatch, getState) => {
  const token = _getToken(getState);
  const before = _sidebarPinned(getState);
  if (!token || from === to || !before[from] || to < 0 || to >= before.length) return;
  const after = [...before];
  after.splice(to, 0, ...after.splice(from, 1));
  dispatch(setChatSidebar({ sidebar_pinned: after }));
  try {
    await _reorderPinnedChats(token, after.map((chat) => String(chat.id)));
  } catch (err) {
    // A later move (or a refresh) has replaced the list since: leave it
    if (getState().chat.sidebar_pinned !== after) return;
    dispatch(setChatSidebar({ sidebar_pinned: before }));
    if (err?.response?.status === 409) {
      Toast.error('Your pinned chats changed in another window. Please try again.');
      dispatch(fetchSidebarChats({ keepLoaded: true }));
    } else {
      Toast.error("Couldn't save the new order. Please try again.");
    }
  }
};

// Tracks the most recently-requested session load. Rapid A→B→C clicks fire
// three loadStreamingSession thunks; without this, the slowest fetch can land
// last and overwrite the user's actual selection.
let _latestRequestedSessionId = null;

// ─────────────────────────────────────────────────────────────────────────────
// loadStreamingSession — load a past ChatSession from history
// ─────────────────────────────────────────────────────────────────────────────
export const loadStreamingSession = (sessionId) => async (dispatch, getState) => {
  const token = _getToken(getState);
  if (!token) return;
  // Tear down any in-flight stream from the session being left, capturing its
  // partial so _executeStream's AbortError path can persist it. Without this,
  // tokens from the abandoned stream bleed into the newly-loaded session.
  _abortStreamForSessionSwitch(dispatch, getState);
  _latestRequestedSessionId = sessionId;
  dispatch(sessionLoadStart());
  try {
    const res = await _getChatSessionMessages(token, sessionId);
    // Drop the response if the user has clicked a different session in the
    // meantime — without this, a slow A request can land after a fast B,C
    // and stomp the freshly-loaded C.
    if (_latestRequestedSessionId !== sessionId) return;
    const { messages = [], title = '' } = res?.data?.data || {};

    // Reshape Django messages into the chatResponse shape used by UnifiedFeed.
    // `attachments` from the detail serializer carries fresh download URLs so
    // historical user messages can render their file tiles.
    const shaped = messages.flatMap((m) => [
      {
        role: 'user',
        question: m.question,
        attachments: m.attachments || [],
        id: m.message_id + '_q',
      },
      {
        role: 'ai',
        answer: m.answer,
        thinking: '',
        references: m.references || [],
        file_downloads: m.file_downloads || [],
        status: m.status,
        failure_reason: m.failure_reason,
        rating: m.rating,
        message_id: m.message_id,
        session_id: sessionId,
        id: m.message_id,
      },
    ]);

    dispatch(loadSessionMessages({ messages: shaped, session_id: sessionId, title }));

    // If the tail message was mid-flight when the session was last saved
    // (status pending/streaming), poll the backend until it transitions to a
    // terminal state. This covers the page-reload-during-stream case where
    // the row sits in `pending` until Kush AI's webhook callback lands —
    // without polling, the user sees a stuck spinner until the 10-min Celery
    // sweep marks the message failed.
    const lastAi = shaped[shaped.length - 1];
    if (lastAi?.role === 'ai' && (lastAi.status === 'pending' || lastAi.status === 'streaming')) {
      _startResumePoll(dispatch, getState, sessionId);
    }
  } catch (_) {
    /* ignore load errors — stay on current view */
  } finally {
    dispatch(sessionLoadEnd());
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// _startResumePoll — when a session loaded with a pending/streaming tail
// message, poll the backend with backoff for up to three minutes. If it reaches a
// terminal state (complete / failed / stopped) the new payload is dispatched
// via loadSessionMessages. After three minutes without resolution polling stops
// and the tail is shown as failed so the user can hit retry. Kush AI stops
// generating when the stream drops and retries its callback for about two
// minutes, so a longer wait only delays the Retry button.
// ─────────────────────────────────────────────────────────────────────────────
let _resumePollTimer = null;
export const RESUME_GAVE_UP_REASON = "This answer didn't finish. Please retry.";
function _startResumePoll(dispatch, getState, sessionId) {
  // Cancel any prior poll so a rapid session-switch can't leave a stale loop
  // mutating the wrong session's state.
  if (_resumePollTimer) {
    clearTimeout(_resumePollTimer);
    _resumePollTimer = null;
  }
  const startedAt = Date.now();
  const MAX_MS = 3 * 60 * 1000;
  const MAX_INTERVAL_MS = 15000;
  let intervalMs = 2000;
  const scheduleNext = () => {
    _resumePollTimer = setTimeout(tick, intervalMs);
    intervalMs = Math.min(Math.round(intervalMs * 1.5), MAX_INTERVAL_MS);
  };
  const tick = async () => {
    _resumePollTimer = null;
    // Bail out if the user switched away from this session in the meantime.
    if (getState()?.chat?.session_id !== sessionId) return;
    const token = _getToken(getState);
    if (!token) return;
    try {
      const res = await _getChatSessionMessages(token, sessionId);
      // Re-check session_id AFTER the fetch resolves — the user may have
      // clicked a different session while we were waiting on the network.
      // Without this guard, a slow poll would overwrite the freshly-loaded
      // session with stale data from the abandoned one.
      if (getState()?.chat?.session_id !== sessionId) return;
      const { messages = [], title = '' } = res?.data?.data || {};
      const tail = messages[messages.length - 1];
      const stillPending = tail && (tail.status === 'pending' || tail.status === 'streaming');
      // Re-shape and dispatch only when status actually changed, to avoid
      // churning chatResponse on every poll. Cheap heuristic: compare status
      // and answer length on the tail message.
      const currentTail = getState()?.chat?.chatResponse?.[getState().chat.chatResponse.length - 1];
      const changed =
        !currentTail ||
        currentTail.status !== tail?.status ||
        (currentTail.answer || '').length !== (tail?.answer || '').length;
      if (changed) {
        const shaped = messages.flatMap((m) => [
          { role: 'user', question: m.question, attachments: m.attachments || [], id: m.message_id + '_q' },
          {
            role: 'ai',
            answer: m.answer,
            thinking: '',
            references: m.references || [],
            file_downloads: m.file_downloads || [],
            status: m.status,
            failure_reason: m.failure_reason,
            rating: m.rating,
            message_id: m.message_id,
            session_id: sessionId,
            id: m.message_id,
          },
        ]);
        dispatch(loadSessionMessages({ messages: shaped, session_id: sessionId, title }));
      }
      if (stillPending && Date.now() - startedAt < MAX_MS && getState()?.chat?.session_id === sessionId) {
        scheduleNext();
      } else if (stillPending && getState()?.chat?.session_id === sessionId) {
        // Poll exhausted without a terminal status — force tail to 'failed' so
        // the retry button appears instead of a permanent pending spinner.
        dispatch(loadSessionMessages({
          session_id: sessionId,
          title,
          messages: messages.flatMap((m) => [
            { role: 'user', question: m.question, attachments: m.attachments || [], id: m.message_id + '_q' },
            {
              role: 'ai',
              answer: m.answer,
              thinking: '',
              references: m.references || [],
              file_downloads: m.file_downloads || [],
              status: m.message_id === tail.message_id ? 'failed' : m.status,
              failure_reason: m.message_id === tail.message_id ? RESUME_GAVE_UP_REASON : (m.failure_reason || ''),
              rating: m.rating,
              message_id: m.message_id,
              session_id: sessionId,
              id: m.message_id,
            },
          ]),
        }));
      }
    } catch (_) { /* keep trying until MAX_MS */
      if (Date.now() - startedAt < MAX_MS && getState()?.chat?.session_id === sessionId) {
        scheduleNext();
      } else if (getState()?.chat?.session_id === sessionId) {
        // Poll exhausted on network error — mark current tail as failed from Redux state
        const current = getState()?.chat?.chatResponse || [];
        const lastAi = current[current.length - 1];
        if (lastAi?.role === 'ai' && (lastAi.status === 'pending' || lastAi.status === 'streaming')) {
          const updated = current.map((m, i) =>
            i === current.length - 1
              ? { ...m, status: 'failed', failure_reason: RESUME_GAVE_UP_REASON }
              : m,
          );
          dispatch(loadSessionMessages({
            messages: updated,
            session_id: sessionId,
            title: getState()?.chat?.title || '',
          }));
        }
      }
    }
  };
  scheduleNext();
}

// ─────────────────────────────────────────────────────────────────────────────
// loadLegacyChat — load a pre-streaming Chat (Q&A pairs) from history
// ─────────────────────────────────────────────────────────────────────────────
export const loadLegacyChat = (item) => async (dispatch, getState) => {
  const token = _getToken(getState);
  if (!token) return;
  // Mirror loadStreamingSession: tear down any in-flight stream from the
  // session being left so its tokens don't bleed into the legacy chat we're
  // about to load.
  _abortStreamForSessionSwitch(dispatch, getState);
  // Invalidate any in-flight streaming-session load so its response can't
  // land on top of this legacy chat.
  _latestRequestedSessionId = item.id || `legacy-${Date.now()}`;
  dispatch(sessionLoadStart());
  try {
    const res = await _chatHistoryContents({ chat_id: item.id }, token);
    const convList = res?.data?.data?.message || [];
    dispatch(setNewChat(false));
    dispatch(setConversationId(item.client_conversation_id));
    dispatch(setTabId(item.id));
    dispatch(streamSessionCreated(null)); // Clear streaming session_id — only one model active at a time.
    dispatch(setPersonality(item.category?.toLowerCase() || 'web'));
    convList.forEach((conv) => {
      dispatch(addChatResponse({
        role: 'user',
        question: conv.question,
        id: conv.id + '_q',
      }));
      dispatch(addChatResponse({
        role: 'ai',
        answer: conv.answer,
        thinking: '',
        references: conv.references || [],
        status: 'complete',
        failure_reason: null,
        rating: null,
        message_id: null,
        session_id: null,
        id: conv.id + '_a',
      }));
    });
  } catch (_) {
    /* ignore load failures */
  } finally {
    dispatch(sessionLoadEnd());
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// startNewChat — clear feed and reset session
// ─────────────────────────────────────────────────────────────────────────────
export const startNewChat = () => (dispatch, getState) => {
  // If a stream is in-flight on the session being left, tear it down and save
  // the partial answer before clearing the feed.
  _abortStreamForSessionSwitch(dispatch, getState);
  // Invalidate any in-flight session load so its response can't overwrite
  // the new (empty) chat.
  _latestRequestedSessionId = null;
  dispatch(setClearChat());
  dispatch(streamSessionCreated(null));
};

// ─────────────────────────────────────────────────────────────────────────────
// ensureChatSession — return the existing session id, or eagerly create one.
// Used by the input bar so per-message attachment uploads (which require a
// session id in the URL) work even before the first message is sent.
// Returns null if there is no auth token (e.g. logged-out edge case).
//
// Dedupes concurrent calls via an in-flight promise so that a user who drops
// five files at once doesn't end up with five fresh ChatSessions.
// ─────────────────────────────────────────────────────────────────────────────
let _ensureSessionInFlight = null;

export const ensureChatSession = () => async (dispatch, getState) => {
  const existing = getState()?.chat?.session_id;
  if (existing) return existing;
  if (_ensureSessionInFlight) return _ensureSessionInFlight;

  const token = _getToken(getState);
  if (!token) return null;

  _ensureSessionInFlight = (async () => {
    try {
      const res = await _createChatSession(token);
      const sessionId = res?.data?.data?.session_id;
      if (sessionId) dispatch(streamSessionCreated(sessionId));
      return sessionId || null;
    } finally {
      _ensureSessionInFlight = null;
    }
  })();
  return _ensureSessionInFlight;
};

// ─────────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────────
const reducer = handleActions(
  {
    // ── Legacy cases ───────────────────────────────────────────────────────
    [ActionTypes.SET_CHAT_RESPONSE]: (state, action) => ({
      ...state,
      chatResponse: [...state.chatResponse, action.payload],
    }),
    [ActionTypes.SET_CLEAR_CHAT]: (state) => ({
      ...state,
      chatResponse: [],
      // Clear both legacy IDs and streaming flags so switching to new chat
      // never leaves stale identifiers from whichever model was active before.
      tab_id: null,
      conversation_id: null,
      new_chat: true,
      is_streaming: false,
      current_message_id: null,
      is_session_loading: false,
    }),
    [ActionTypes.SET_LOADING]: (state, action) => ({
      ...state,
      loading: action.payload,
    }),
    [ActionTypes.SET_PERSONALITY]: (state, action) => ({
      ...state,
      personality: action.payload,
    }),
    [ActionTypes.SET_CONVERSATION_ID]: (state, action) => ({
      ...state,
      conversation_id: action.payload,
    }),
    [ActionTypes.SET_SEARCH_QUERY]: (state, action) => ({
      ...state,
      search_query: action.payload,
    }),
    [ActionTypes.SET_TAB_ID]: (state, action) => ({
      ...state,
      tab_id: action.payload,
    }),
    [ActionTypes.SET_CHAT_SIDEBAR]: (state, action) => ({
      ...state,
      ...action.payload,
    }),
    [ActionTypes.SET_NEW_CHAT]: (state, action) => ({
      ...state,
      new_chat: action.payload,
    }),
    [ActionTypes.SET_CHAT_COUNTRY]: (state, action) => ({
      ...state,
      country: action.payload,
    }),
    [ActionTypes.SET_CHAT_REGION]: (state, action) => ({
      ...state,
      region: action.payload,
    }),
    [ActionTypes.SET_CHAT_TOPIC]: (state, action) => ({
      ...state,
      topic: action.payload,
    }),
    [ActionTypes.SET_PREVIOUS_TEXT]: (state, action) => ({
      ...state,
      previous_text: action.payload,
    }),
    [ActionTypes.SET_PAGINATE_COUNT]: (state, action) => ({
      ...state,
      paginate_count: action.payload,
    }),
    [ActionTypes.SET_GENERATE_MORE]: (state, action) => ({
      ...state,
      generate_more: action.payload,
    }),
    [ActionTypes.SET_CANCEL_API]: (state, action) => ({
      ...state,
      cancel_api: action.payload,
    }),
    [ActionTypes.SET_CHAT_PERMISSION]: (state, action) => ({
      ...state,
      permission: action.payload,
    }),
    [ActionTypes.SET_UPLOADDOC_LOADING]: (state, action) => ({
      ...state,
      uploadDocLoading: action.payload,
    }),
    [ActionTypes.SET_UPLOADEDDOC]: (state, action) => ({
      ...state,
      uploadedDocs: action.payload || [],
    }),
    [ActionTypes.SET_DOCSUBMIT_LOADING]: (state, action) => ({
      ...state,
      docSubmitLoading: action.payload,
    }),
    [ActionTypes.SET_DOC_URL]: (state, action) => ({
      ...state,
      docUrl: action.payload || [],
    }),

    // ── Streaming cases (Task 7) ───────────────────────────────────────────
    [ActionTypes.STREAM_SESSION_CREATED]: (state, action) => ({
      ...state,
      session_id: action.payload,
    }),

    [ActionTypes.STREAM_INIT]: (state, action) => ({
      ...state,
      is_streaming: true,
      current_message_id: null,
      chatResponse: [
        ...state.chatResponse,
        // User message placeholder (with optimistic attachment tiles)
        {
          role: 'user',
          question: action.payload.question,
          attachments: action.payload.attachments || [],
          id: Date.now() + '_q',
        },
        // AI response placeholder — populated by STREAM_TOKEN / STREAM_THINKING
        {
          role: 'ai',
          answer: '',
          thinking: '',
          references: [],
          file_downloads: [],
          status: 'streaming',
          failure_reason: null,
          rating: null,
          message_id: null,
          session_id: state.session_id,
          id: Date.now() + '_a',
        },
      ],
    }),

    // Replace a user+AI pair in-place (retry / refresh).
    // pairIndex is the index of the user message; everything from pairIndex
    // onward is dropped and replaced with fresh placeholders.
    [ActionTypes.STREAM_RETRY_INIT]: (state, action) => {
      const { question, pairIndex, messageId, attachments } = action.payload;
      const cutAt = pairIndex != null ? pairIndex : state.chatResponse.length - 2;
      return {
        ...state,
        is_streaming: true,
        current_message_id: messageId || null,
        chatResponse: [
          ...state.chatResponse.slice(0, cutAt),
          { role: 'user', question, attachments: attachments || [], id: Date.now() + '_q' },
          {
            role: 'ai',
            answer: '',
            thinking: '',
            references: [],
            file_downloads: [],
            status: 'streaming',
            failure_reason: null,
            rating: null,
            // Preserve the original message_id (backend reuses the same UUID
            // on retry) so the retry buttons remain functional even if the
            // credentials request fails before the stream starts.
            message_id: messageId || null,
            session_id: state.session_id,
            id: Date.now() + '_a',
          },
        ],
      };
    },

    [ActionTypes.STREAM_TOKEN]: (state, action) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.answer = (last.answer || '') + action.payload;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages };
    },

    [ActionTypes.STREAM_THINKING]: (state, action) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.thinking = (last.thinking || '') + action.payload;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages };
    },

    [ActionTypes.STREAM_REFERENCES]: (state, action) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.references = action.payload;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages };
    },

    [ActionTypes.STREAM_FILE_DOWNLOADS]: (state, action) => {
      // Defensive: only mutate when the tail is an AI placeholder. The parser
      // currently dispatches this only mid-stream (so STREAM_INIT has already
      // appended the AI scaffold), but guarding here keeps the reducer total
      // against a future caller that fires before STREAM_INIT.
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      if (last?.role !== 'ai') return state;
      last.file_downloads = action.payload;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages };
    },

    [ActionTypes.STREAM_DONE]: (state) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.status = 'complete';
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages, is_streaming: false };
    },

    [ActionTypes.STREAM_ERROR]: (state, action) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.status = 'failed';
      last.failure_reason = action.payload || 'An error occurred.';
      last.error_code = 'NETWORK';
      last.technical_details = action.payload || null;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages, is_streaming: false };
    },

    [ActionTypes.STREAM_INLINE_ERROR]: (state, action) => {
      const { code, friendlyMessage, technicalDetails, rollbackChars = 0 } = action.payload;
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      // The error blob may have been partially appended to msg.answer in
      // earlier chunks before the sentinel completed. Roll it back.
      if (rollbackChars > 0 && last.answer) {
        last.answer = last.answer.slice(0, Math.max(0, last.answer.length - rollbackChars));
      }
      last.status = 'failed';
      last.failure_reason = friendlyMessage;
      last.error_code = code;
      last.technical_details = technicalDetails;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages, is_streaming: false };
    },

    [ActionTypes.STREAM_STOPPED]: (state, action) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.status = action.payload?.reason === 'user_stop' ? 'stopped' : 'pending';
      last.answer = action.payload?.partial_answer ?? last.answer;
      messages[messages.length - 1] = last;
      return { ...state, chatResponse: messages, is_streaming: false };
    },

    [ActionTypes.MESSAGE_RATING_SET]: (state, action) => {
      const { message_id, rating } = action.payload;
      if (!message_id) return state;
      const idx = state.chatResponse.findIndex(
        (m) => m.role === 'ai' && m.message_id === message_id,
      );
      if (idx === -1) return state;
      const messages = [...state.chatResponse];
      messages[idx] = { ...messages[idx], rating };
      return { ...state, chatResponse: messages };
    },

    [ActionTypes.LOAD_SESSION_MESSAGES]: (state, action) => ({
      ...state,
      chatResponse: action.payload.messages,
      session_id: action.payload.session_id,
      // Clear legacy IDs — only one chat model should be active at a time.
      tab_id: null,
      conversation_id: null,
      is_streaming: false,
      is_session_loading: false,
      current_message_id: null,
    }),

    // Click-to-switch starts here: wipe the feed so previous chat's bubbles
    // disappear in the same tick the click registers. Don't touch is_streaming —
    // any in-flight stream's terminal action handles its own state.
    [ActionTypes.SESSION_LOAD_START]: (state) => ({
      ...state,
      is_session_loading: true,
      chatResponse: [],
      current_message_id: null,
    }),

    // Idempotent safety-net used in thunk finally blocks for the error path;
    // on success, LOAD_SESSION_MESSAGES already clears the flag atomically.
    [ActionTypes.SESSION_LOAD_END]: (state) => ({
      ...state,
      is_session_loading: false,
    }),

    [ActionTypes.COMPLETION_SAVE_START]: (state) => ({
      ...state,
      is_saving_completion: true,
    }),

    [ActionTypes.COMPLETION_SAVE_END]: (state) => ({
      ...state,
      is_saving_completion: false,
    }),

    // Set message_id on the most recent AI placeholder after Django confirms creation
    [ActionTypes.STREAM_MESSAGE_ID_SET]: (state, action) => {
      const messages = [...state.chatResponse];
      const last = { ...messages[messages.length - 1] };
      last.message_id = action.payload.message_id;
      last.session_id = action.payload.session_id;
      messages[messages.length - 1] = last;
      return {
        ...state,
        chatResponse: messages,
        current_message_id: action.payload.message_id,
        session_id: action.payload.session_id,
      };
    },
  },
  initialState,
);

export default reducer;
